import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { COMPANY, intlPhone, isEnglishPath } from '../../pages/en/enCommon';
import { Box, Link, Stack, Typography } from '@mui/material';

/** 공정거래위원회 사업자정보 공개 페이지. 사업자번호(숫자만)를 붙여 연다. */
const FTC_BIZ_INFO_URL = 'https://www.ftc.go.kr/bizCommPop.do?wrkr_no=';

/**
 * 사이트 하단 링크.
 * 전자상거래법상 표시 항목이고 전자결제(토스페이먼츠) 심사가 본다(2026-10-09).
 * 개인정보처리방침은 법상 다른 링크와 구분되게(굵게) 보여야 한다.
 */
const POLICY_LINKS = [
  { to: '/terms', label: '이용약관' },
  { to: '/privacy', label: '개인정보처리방침', strong: true },
  { to: '/center#refund', label: '취소·환불규정' },
  { to: '/center', label: '고객센터' },
];

const lineSx = {
  fontSize: { xs: 11, sm: 13, md: 14 },
  lineHeight: 1.7,
  whiteSpace: 'nowrap',
};

const linkSx = { color: 'inherit', textUnderlineOffset: 3 };

/**
 * 하단 회사 정보.
 * 모바일에서는 각 항목을 줄바꿈 없이 "전부 한 줄"로 표시한다(넘치면 가로 스크롤).
 */
export default function AppFooter({ companyData = {} }) {
  const { pathname } = useLocation();
  const english = isEnglishPath(pathname);
  const businessNum = companyData.businessNum || '';
  const bizDigits = businessNum.replace(/[^0-9]/g, '');
  const mailOrderNum = companyData.mailOrderNum || '';

  // 영문 화면 (2026-11-04). 정책 문서는 한국어뿐이라 그렇다고 적어 둔다
  const enLinks = [
    { to: '/en/center', label: 'Get a quote' },
    { to: '/terms', label: 'Terms (Korean)' },
    { to: '/privacy', label: 'Privacy policy (Korean)', strong: true },
  ];
  const enLines = [
    `${COMPANY} · CEO ${companyData.adminName || ''} · Business registration no. ${businessNum}`,
    'Licensed general travel agency (domestic / international travel), Republic of Korea',
    `Address: ${companyData.address || ''}`,
    `Tel ${intlPhone(companyData.tel)} · Email ${companyData.email || ''}`,
    'copyright. www.ekhanabus.com All Right Reserved.',
  ];

  const lines = english ? enLines : [
    `주식회사 이케이투어 · 대표 ${companyData.adminName || ''} · 개인정보 보호책임자 ${companyData.infoHandlerName || ''}`,
    <>
      사업자번호 {businessNum}
      {/* 신고번호를 관리자 환경설정에 넣기 전에는 이 항목을 비워 두지 않고 뺀다 */}
      {mailOrderNum && ` · 통신판매업 신고 ${mailOrderNum}`}
      {bizDigits && (
        <>
          {' · '}
          <Link href={FTC_BIZ_INFO_URL + bizDigits} target='_blank' rel='noopener noreferrer' sx={linkSx}>
            사업자정보확인
          </Link>
        </>
      )}
    </>,
    `일반여행업등록법인 (국내여행/해외여행) · ${companyData.address || ''}`,
    `대표전화 ${companyData.tel || ''} · 팩스 ${companyData.fax || ''} · 버스대절 문의 ${companyData.phone || ''}`,
    `${companyData.email || ''} · 호스팅 서비스 제공자 Oracle Corporation`,
    'copyright. www.ekhanabus.com All Right Reserved.',
  ];

  return (
    <Box
      component='footer'
      sx={{
        backgroundColor: '#EC9F46',
        color: '#FCFCFC',
        mt: 'auto',
        px: { xs: 1.5, md: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Stack
        spacing={{ xs: 0.6, md: 0.8 }}
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          // 모바일: 한 줄씩, 넘치면 그 줄만 가로 스크롤
          overflowX: 'auto',
        }}
      >
        <Typography component='nav' sx={{ ...lineSx, mb: { xs: 0.4, md: 0.6 } }}>
          {(english ? enLinks : POLICY_LINKS).map((item, i) => (
            <React.Fragment key={item.to}>
              {i > 0 && ' | '}
              <Link component={RouterLink} to={item.to} sx={{ ...linkSx, fontWeight: item.strong ? 700 : 400 }}>
                {item.label}
              </Link>
            </React.Fragment>
          ))}
        </Typography>

        {lines.map((line, i) => (
          <Typography key={i} noWrap sx={lineSx}>
            {line}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}
